import { motion } from "framer-motion";
import { CircleDollarSign, MessageCircle, ShieldCheck } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";

const WHATSAPP_LINK =
  "https://wa.me/966540423401?text=Hello%20BlackRide%0A%0AI%20would%20like%20to%20get%20a%20quote%20for%20a%20private%20transfer.%0A%0APickup%3A%0ADrop%3A%0ADate%20%26%20Time%3A%0APassengers%3A%0ALuggage%3A";

const COPY = {
  en: {
    eyebrow: "Pricing Guidance",
    title: "Clearer fare expectations for the routes guests ask about most.",
    note:
      "Final pricing depends on route, timing, vehicle class, and luggage needs, but BlackRide helps guests understand the likely transfer level before they confirm.",
    cta: "Request a tailored quote on WhatsApp",
    rows: [
      { route: "Jeddah Airport → Makkah", price: "From 200 SAR", detail: "Best for direct airport-to-hotel arrivals." },
      { route: "Jeddah Airport → Jeddah City", price: "From 200 SAR", detail: "Useful for business arrivals, hotels, and hosted city transfers." },
      { route: "Hourly chauffeur service", price: "From 100 SAR / hour", detail: "Suitable for executive schedules, hosted guests, and flexible city movement." },
    ],
    footer: "For families, VIP arrivals, SUVs, executive vans, and intercity routes, the best quote is usually the one built around the real trip details.",
  },
  ar: {
    eyebrow: "مؤشرات الأسعار",
    title: "توقعات أوضح للأسعار في المسارات التي يطلبها الضيوف أكثر من غيرها.",
    note:
      "يعتمد السعر النهائي على المسار والتوقيت وفئة المركبة واحتياجات الأمتعة، لكن بلاك رايد تساعد الضيف على فهم المستوى السعري المتوقع قبل التأكيد.",
    cta: "اطلب سعراً مخصصاً عبر واتساب",
    rows: [
      { route: "مطار جدة ← مكة", price: "من 200 ريال", detail: "الأفضل للوصول المباشر من المطار إلى الفندق." },
      { route: "مطار جدة ← داخل جدة", price: "من 200 ريال", detail: "مناسب للوصولات التنفيذية والفنادق والتنقلات داخل المدينة." },
      { route: "سائق بالساعة", price: "من 100 ريال / ساعة", detail: "مناسب للجداول التنفيذية والضيوف المهمين والتنقل المرن داخل المدينة." },
    ],
    footer: "بالنسبة للعائلات وكبار الشخصيات وSUV والفانات التنفيذية والتنقل بين المدن، يكون أفضل تسعير عادة هو المبني على تفاصيل الرحلة الفعلية.",
  },
};

export default function PricingSection() {
  const { lang } = useLanguage();
  const copy = COPY[lang] || COPY.en;

  return (
    <section className="bg-[#060606] py-20 md:py-24">
      <div className="mx-auto max-w-7xl px-6 md:px-12">
        <div className="rounded-[34px] border border-white/10 bg-[#121212] p-8 md:p-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.55 }}
            className="mb-10"
          >
            <p className="mb-4 text-xs font-medium uppercase tracking-[0.25em] text-[#C7A239]">{copy.eyebrow}</p>
            <h2 className="max-w-4xl font-['Playfair_Display'] text-3xl font-semibold leading-tight text-white md:text-4xl">
              {copy.title}
            </h2>
            <p className="mt-4 max-w-3xl text-base leading-8 text-[#b7aea1]">{copy.note}</p>
          </motion.div>

          <div className="grid gap-4 md:grid-cols-3">
            {copy.rows.map((row, index) => (
              <motion.div
                key={row.route}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.45, delay: index * 0.06 }}
                className="rounded-[26px] border border-white/8 bg-[#0b0b0b] p-6"
              >
                <CircleDollarSign className="mb-4 h-5 w-5 text-[#C7A239]" />
                <h3 className="text-xl font-medium text-white">{row.route}</h3>
                <p className="mt-3 text-2xl font-semibold text-[#D7B458]">{row.price}</p>
                <p className="mt-3 text-sm leading-7 text-[#9f978b]">{row.detail}</p>
              </motion.div>
            ))}
          </div>

          <div className="mt-8 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <p className="flex max-w-3xl items-start gap-3 text-sm leading-7 text-[#a79f93]">
              <ShieldCheck className="mt-1 h-4.5 w-4.5 shrink-0 text-[#C7A239]" />
              <span>{copy.footer}</span>
            </p>
            <a
              href={WHATSAPP_LINK}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 text-[#C7A239] transition-colors hover:text-[#D4B34A]"
            >
              <MessageCircle className="h-4 w-4" />
              {copy.cta}
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
